<template>
  <div class="dashboard-container">
    <el-row :gutter="24">
      <el-col :span="24">
        <div class="welcome-card">
          <h2>欢迎使用商家端</h2>
          <p>今天是 {{ todayDate }}，祝您工作愉快！</p>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="24" class="statistics-row">
      <el-col :span="4">
        <el-card shadow="never" class="stat-card">
          <router-link to="/merchant/orders/not-shipped" class="stat-link">
            <div class="stat-header">
              <i class="el-icon-document stat-icon"></i>
              <span class="stat-title">未邮寄订单</span>
            </div>
            <div class="stat-value">{{ orderStats.notShipped || 0 }}</div>
          </router-link>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="never" class="stat-card">
          <router-link to="/merchant/orders/in-transit" class="stat-link">
            <div class="stat-header">
              <i class="el-icon-truck stat-icon"></i>
              <span class="stat-title">在途订单</span>
            </div>
            <div class="stat-value">{{ orderStats.inTransit || 0 }}</div>
          </router-link>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="never" class="stat-card">
          <router-link to="/merchant/orders/testing" class="stat-link">
            <div class="stat-header">
              <i class="el-icon-loading stat-icon"></i>
              <span class="stat-title">测试中订单</span>
            </div>
            <div class="stat-value">{{ orderStats.testing || 0 }}</div>
          </router-link>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="never" class="stat-card">
          <router-link to="/merchant/orders/tested" class="stat-link">
            <div class="stat-header">
              <i class="el-icon-check stat-icon"></i>
              <span class="stat-title">已测试订单</span>
            </div>
            <div class="stat-value">{{ orderStats.tested || 0 }}</div>
          </router-link>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="never" class="stat-card">
          <router-link to="/merchant/orders/completed" class="stat-link">
            <div class="stat-header">
              <i class="el-icon-circle-check stat-icon"></i>
              <span class="stat-title">已完成订单</span>
            </div>
            <div class="stat-value">{{ orderStats.completed || 0 }}</div>
          </router-link>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="never" class="stat-card">
          <router-link to="/merchant/orders/dispute" class="stat-link">
            <div class="stat-header">
              <i class="el-icon-warning stat-icon"></i>
              <span class="stat-title">争议/复测订单</span>
            </div>
            <div class="stat-value">{{ orderStats.dispute || 0 }}</div>
          </router-link>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="24" class="statistics-row">
      <el-col :span="8">
        <el-card shadow="never" class="amount-card">
          <div class="amount-title">总订单金额</div>
          <div class="amount-value">¥{{ formatNumber(orderAmount.total || 0) }}</div>
          <div class="amount-footer">
            <el-progress :percentage="100" :show-text="false" :stroke-width="8" status="success"></el-progress>
          </div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card shadow="never" class="amount-card">
          <div class="amount-title">未结算订单金额</div>
          <div class="amount-value">¥{{ formatNumber(orderAmount.unsettled || 0) }}</div>
          <div class="amount-footer">
            <el-progress :percentage="orderAmount.unsettledPercentage || 0" :show-text="false" :stroke-width="8" status="warning"></el-progress>
            <div class="amount-desc">占总金额的 {{ orderAmount.unsettledPercentage || 0 }}%</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="8">
        <el-card shadow="never" class="amount-card">
          <div class="amount-title">本月订单金额</div>
          <div class="amount-value">¥{{ formatNumber(orderAmount.thisMonth || 0) }}</div>
          <div class="amount-footer">
            <el-progress :percentage="orderAmount.thisMonthPercentage || 0" :show-text="false" :stroke-width="8" color="#409EFF"></el-progress>
            <div class="amount-desc">占总金额的 {{ orderAmount.thisMonthPercentage || 0 }}%</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="24">
      <el-col :span="24">
        <el-card shadow="never" class="list-card">
          <div slot="header" class="list-header">
            <span>最近订单</span>
            <router-link to="/merchant/orders/all" class="view-more">
              查看全部 <i class="el-icon-arrow-right"></i>
            </router-link>
          </div>
          <div v-if="loading" class="loading-container">
            <el-skeleton :rows="4" animated />
          </div>
          <div v-else-if="recentOrders.length === 0" class="empty-list">
            <i class="el-icon-document empty-icon"></i>
            <span>暂无订单</span>
          </div>
          <el-table v-else :data="recentOrders" style="width: 100%" size="mini">
            <el-table-column width="100" label="订单号" prop="id"></el-table-column>
            <el-table-column label="客户" prop="customer"></el-table-column>
            <el-table-column width="100" label="状态">
              <template slot-scope="scope">
                <el-tag size="mini" :type="getStatusType(scope.row.orderStatusName)">{{ scope.row.orderStatusName }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column width="100" label="金额">
              <template slot-scope="scope">
                {{ formatMoney(scope.row.amount) }}
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="24">
      <el-col :span="24">
        <el-card shadow="never" class="list-card">
          <div slot="header" class="list-header">
            <span>即将/已超期订单</span>
            <router-link to="/merchant/orders/expiring" class="view-more">
              查看全部 <i class="el-icon-arrow-right"></i>
            </router-link>
          </div>
          <div v-if="loading" class="loading-container">
            <el-skeleton :rows="4" animated />
          </div>
          <div v-else-if="expiringOrders.length === 0" class="empty-list">
            <i class="el-icon-circle-check empty-icon"></i>
            <span>暂无超期订单</span>
          </div>
          <el-table v-else :data="expiringOrders" style="width: 100%" size="mini">
            <el-table-column width="100" label="订单号" prop="id"></el-table-column>
            <el-table-column label="客户" prop="customer" show-overflow-tooltip></el-table-column>
            <el-table-column label="预计完成时间" width="110">
              <template slot-scope="scope">
                {{ formatDate(scope.row.expectedEndTime) }}
              </template>
            </el-table-column>
            <el-table-column label="超期情况" width="100">
              <template slot-scope="scope">
                <el-tag size="mini" :type="scope.row.daysInfo && scope.row.daysInfo.includes('已超期') ? 'danger' : 'warning'">
                  {{ scope.row.daysInfo || scope.row.status }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column width="80" label="状态">
              <template slot-scope="scope">
                <el-tag size="mini" type="info">{{ scope.row.orderStatusName }}</el-tag>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>
    
    <el-row :gutter="24">
      <el-col :span="24">
        <el-card shadow="never" class="list-card">
          <div slot="header" class="list-header">
            <span>争议/复测订单</span>
            <router-link to="/merchant/orders/dispute" class="view-more">
              查看全部 <i class="el-icon-arrow-right"></i>
            </router-link>
          </div>
          <div v-if="loading" class="loading-container">
            <el-skeleton :rows="4" animated />
          </div>
          <div v-else-if="disputeOrders.length === 0" class="empty-list">
            <i class="el-icon-circle-check empty-icon"></i>
            <span>暂无争议/复测订单</span>
          </div>
          <el-table v-else :data="disputeOrders" style="width: 100%" size="mini">
            <el-table-column width="100" label="订单号" prop="id"></el-table-column>
            <el-table-column label="客户" prop="customer" show-overflow-tooltip></el-table-column>
            <el-table-column label="类型" width="80">
              <template slot-scope="scope">
                <el-tag size="mini" :type="scope.row.orderType === '争议订单' ? 'danger' : 'warning'">
                  {{ scope.row.orderType }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="仪器名称" prop="deviceName" show-overflow-tooltip></el-table-column>
            <el-table-column label="仪器型号" prop="deviceModel" show-overflow-tooltip></el-table-column>
            <el-table-column width="80" label="状态">
              <template slot-scope="scope">
                <el-tag size="mini" type="info">{{ scope.row.orderStatusName }}</el-tag>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import adminApi from '@/api/adminApi'

export default {
  name: 'MerchantDashboard',
  data() {
    return {
      todayDate: new Date().toLocaleDateString('zh-CN', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric', 
        weekday: 'long' 
      }),
      loading: false,
      // 订单状态统计
      orderStats: {
        notShipped: 0,
        inTransit: 0,
        testing: 0,
        tested: 0,
        completed: 0,
        dispute: 0,
        expiring: 0,
        unsettled: 0
      },
      // 订单金额统计
      orderAmount: {
        total: 0,
        unsettled: 0,
        thisMonth: 0,
        unsettledPercentage: 0,
        thisMonthPercentage: 0
      },
      // 最近订单列表
      recentOrders: [],
      // 即将/已超期订单列表
      expiringOrders: [],
      // 争议/复测订单列表
      disputeOrders: []
    }
  },
  created() {
    this.fetchDashboardData()
  },
  methods: {
    formatNumber(num) {
      if (num === undefined || num === null) return "0";
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    formatMoney(amount) {
      if (!amount && amount !== 0) return '-';
      return '¥' + this.formatNumber(amount);
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    },
    getStatusType(status) {
      const statusMap = {
        '未邮寄': 'info',
        '在途': 'primary',
        '测试中': 'warning',
        '已测试': 'success',
        '已完成': 'success',
        '复测中': 'danger',
        '争议中': 'danger'
      };
      return statusMap[status] || 'info';
    },
    async fetchDashboardData() {
      this.loading = true
      try {
        const response = await adminApi.getDashboardData()
        console.log('商家端仪表盘数据:', response)
        
        const data = response
        
        // 更新订单状态统计
        if (data.orderStats) {
          this.orderStats = data.orderStats
        }
        
        // 更新订单金额统计
        if (data.orderAmount) {
          this.orderAmount = data.orderAmount
        }
        
        // 更新最近订单列表
        if (Array.isArray(data.recentOrders)) {
          this.recentOrders = data.recentOrders
        }
        
        // 更新即将/已超期订单列表
        if (Array.isArray(data.expiringOrders)) {
          this.expiringOrders = data.expiringOrders
        }
        
        // 更新复测/争议订单列表
        if (Array.isArray(data.disputeOrders)) {
          this.disputeOrders = data.disputeOrders
        }
      } catch (error) {
        console.error('获取仪表盘数据失败:', error)
        this.$message.error('获取仪表盘数据失败，请刷新页面重试')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.dashboard-container {
  padding: 20px;
}

/* 欢迎卡片样式 */
.welcome-card {
  background: linear-gradient(to right, #1890ff, #36cfc9);
  padding: 20px;
  border-radius: 4px;
  color: white;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.welcome-card h2 {
  margin: 0;
  font-size: 24px;
}

.welcome-card p {
  margin-top: 10px;
  font-size: 14px;
  opacity: 0.8;
}

/* 统计卡片行样式 */
.statistics-row {
  margin-bottom: 20px;
}

/* 订单数量统计卡片样式 */
.stat-card {
  height: 120px;
  overflow: hidden;
  transition: all 0.3s;
  cursor: pointer;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.stat-link {
  display: block;
  height: 100%;
  text-decoration: none;
  color: inherit;
}

.stat-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 10px;
}

.stat-icon {
  font-size: 30px;
  margin-bottom: 5px;
  color: #409EFF;
}

.stat-title {
  font-size: 14px;
  color: #606266;
}

.stat-value {
  text-align: center;
  font-size: 28px;
  font-weight: bold;
  color: #303133;
}

/* 金额统计卡片样式 */
.amount-card {
  padding: 15px;
  height: 140px;
}

.amount-title {
  font-size: 15px;
  color: #909399;
  margin-bottom: 10px;
}

.amount-value {
  font-size: 26px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 20px;
}

.amount-footer {
  margin-top: 10px;
}

.amount-desc {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
  text-align: right;
}

/* 列表卡片样式 */
.list-card {
  margin-bottom: 20px;
  height: 265px;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.view-more {
  font-size: 12px;
  color: #409EFF;
  text-decoration: none;
}

.empty-list {
  height: 150px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #909399;
}

.empty-icon {
  font-size: 40px;
  margin-bottom: 10px;
  color: #DCDFE6;
}

.loading-container {
  padding: 20px;
  min-height: 150px;
}

::v-deep .el-table {
  border-radius: 4px;
  overflow: hidden;
}

::v-deep .el-table th {
  background-color: #f5f7fa;
  color: #606266;
  padding: 8px 0;
}
</style> 